<template>
  <div>
    <div class="d-flex align-items-center">
      <button
        class="btn btn-outline-primary"
        type="button"
        title="Add User"
        @click="openModal()"
      >
        <AddUserIcon />
      </button>
      <div
        v-if="adding"
        class="spinner spinner-border text-primary-light mx-3"
      ></div>
    </div>
    <transition name="fade">
      <div
        v-if="modalOpen"
        id="addUserModal"
        class="modal show"
        tabindex="-1"
        aria-labelledby="addUserModalLabel"
        aria-hidden="true"
      >
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h3 id="addUserModalLabel" class="modal-title">
                Add User &mdash;
                {{ stage.charAt(0).toUpperCase() + stage.slice(1) }}
              </h3>
              <button
                type="button"
                class="close"
                aria-label="Close"
                @click="modalOpen = false"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <!-- Search Panel -->
            <div v-show="stage === 'search'">
              <div class="modal-body">
                <div class="d-flex flex-column align-items-start">
                  <label for="user-id"
                    >ID (EmplID, NetID, CatCard number, or email):</label
                  >
                  <input
                    id="user-id"
                    v-model="userId"
                    class="form-control mb-4"
                    :disabled="!!lastName.length || !!firstName.length"
                    type="text"
                  />
                  <h4 class="w-100 d-flex justify-content-center">
                    &mdash; Or &mdash;
                  </h4>
                  <label for="last-name" class="mt-3">Last Name:</label>
                  <input
                    id="last-name"
                    v-model="lastName"
                    type="text"
                    class="form-control"
                    :disabled="!!userId.length"
                  />
                  <label for="first-name" class="mt-3">First Name:</label>
                  <input
                    id="first-name"
                    v-model="firstName"
                    type="text"
                    class="form-control"
                    :disabled="!!userId.length"
                  />
                </div>
              </div>
              <div class="modal-footer">
                <div class="w-100 d-flex flex-column">
                  <div
                    v-if="validationMessage"
                    class="alert alert-warning fade"
                    :class="{ show: validationMessage }"
                    role="alert"
                  >
                    {{ validationMessage }}
                    <button
                      type="button"
                      class="close"
                      aria-label="Close"
                      @click="validationMessage = null"
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                  <div class="d-flex">
                    <button
                      id="close-modal"
                      type="button"
                      class="btn btn-secondary d-flex"
                      title="Cancel"
                      @click="modalOpen = false"
                    >
                      <CancelIcon
                        class="mr-1"
                        style="width: 15px;"
                        viewBox="0 0 24 24"
                      />
                      Cancel
                    </button>
                    <button
                      id="close-modal"
                      type="button"
                      class="btn btn-outline-warning ml-auto d-flex"
                      title="Clear"
                      @click="clearSearch()"
                    >
                      <ClearIcon
                        class="mr-1"
                        style="width: 15px;"
                        viewBox="0 0 24 24"
                      />
                      Clear
                    </button>
                    <button
                      id="search"
                      type="button"
                      class="btn btn-primary ml-2"
                      title="Search"
                      :disabled="searching"
                      @click="search"
                    >
                      <div
                        v-if="searching"
                        class="spinner spinner-grow spinner-grow-sm px-2"
                      ></div>
                      <span v-else>
                        <SearchIcon style="width: 13px;" viewBox="0 0 24 24" />
                        Search
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <!-- Confirm Panel -->
            <div v-show="stage == 'confirm'">
              <div v-if="resultsList" class="modal-body">
                <div v-if="resultsList.length > 1">
                  <select v-model="emplid" class="custom-select mb-4">
                    <option selected :value="null"
                      >&mdash; Select person to add &mdash;</option
                    >
                    <option
                      v-for="person in resultsList"
                      :key="person.emplId"
                      :value="person.emplId"
                    >
                      {{ person.cn }}
                      <span v-if="person.uid"> ({{ person.uid }}) </span>
                    </option>
                  </select>
                </div>
                <div v-else-if="resultsList.length === 1">
                  <h3>
                    {{ resultsList[0].cn }}
                    <span v-if="resultsList[0].uid"
                      >({{ resultsList[0].uid }})</span
                    >
                  </h3>
                  Add {{ resultsList[0].cn }} as a user?
                </div>
              </div>
              <div class="modal-footer">
                <div class="w-100 d-flex flex-column">
                  <div
                    v-if="validationMessage"
                    class="alert alert-warning fade"
                    :class="{ show: validationMessage }"
                    role="alert"
                  >
                    {{ validationMessage }}
                    <button
                      type="button"
                      class="close"
                      aria-label="Close"
                      @click="validationMessage = null"
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                  <div class="d-flex">
                    <button
                      id="back"
                      type="button"
                      class="btn btn-secondary d-flex"
                      title="Back to Search"
                      @click.prevent="stage = 'search'"
                    >
                      <BackIcon
                        class="mr-1"
                        style="width: 15px;"
                        viewBox="0 0 24 24"
                      />
                      Back
                    </button>
                    <button
                      id="confirm"
                      type="button"
                      class="btn btn-primary ml-auto"
                      title="Confirm Add User"
                      :disabled="!emplid"
                      @click="confirm"
                    >
                      <ConfirmIcon
                        class="mr-1"
                        style="width: 15px;"
                        viewBox="0 0 24 24"
                      />
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ClearIcon from "../assets/wind.svg"
import CancelIcon from "../assets/x.svg"
import AddUserIcon from "../assets/user-plus.svg"
import SearchIcon from "../assets/search.svg"
import BackIcon from "../assets/arrow-left.svg"
import ConfirmIcon from "../assets/user-check.svg"
export default {
  components: {
    ClearIcon,
    CancelIcon,
    AddUserIcon,
    SearchIcon,
    BackIcon,
    ConfirmIcon,
  },
  props: {
    adding: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      modalOpen: false,
      stage: "search",
      firstName: "",
      lastName: "",
      userId: "",
      validationMessage: null,
      resultsList: null,
      searching: false,
      emplid: null,
    }
  },
  methods: {
    async search() {
      this.resetModal()
      if ((!this.firstName || !this.lastName) && !this.userId) {
        this.validationMessage =
          "Please enter ID or First Name / Last Name to search"
        return
      }
      let path, message
      if (this.userId) {
        // prefer user ID
        path = `/eds/person/${this.userId}/`
        message = `No user found with id '${this.userId}'`
      } else {
        // otherwise use first/last names
        path = `/eds/search/${this.lastName}/${this.firstName}/`
        message = `No user found with name '${this.lastName}, ${this.firstName}'`
      }
      this.searching = true
      const [_error, response] = await this.$api.get(path)
      if (!response.data.length) {
        // no users found returns empty list
        this.validationMessage = message
      } else {
        this.emplid = response.data.length > 1 ? null : response.data[0].emplId
        this.resultsList = response.data
        this.stage = "confirm"
      }
      this.searching = false
    },
    confirm() {
      this.$emit("input", this.emplid)
      this.modalOpen = false
      this.resetModal()
    },
    openModal() {
      this.validationMessage = null
      this.modalOpen = true
    },
    resetModal() {
      this.validationMessage = null
      this.resultsList = null
      this.emplid = null
      this.searching = false
      this.stage = "search"
    },
    clearSearch() {
      this.firstName = ""
      this.lastName = ""
      this.userId = ""
    },
  },
}
</script>

<style lang="postcss" scoped>
.modal.show {
  height: calc(100vh + 40px);
  background: rgba(0, 0, 0, 0.5);
  display: block !important;
}
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}
.fade-enter,
.fade-leave-to {
  transform: translateY(-40px);
  opacity: 0;
}
</style>
